import { z } from 'zod';

// Treats an empty string the same as "unset" — Vite leaves `VITE_*` vars that
// are present-but-blank in `.env` as `''`, and we want those to fall back to
// the schema default rather than fail `.url()`. Generic over the wrapped
// schema so the inferred output keeps its concrete type — zod 4's `z.ZodType`
// defaults its output to `unknown`, so an un-parameterised parameter would
// widen `clientEnv`'s fields to `unknown`.
const optionalNonEmpty = <T extends z.ZodType>(schema: T) =>
  z.preprocess((v) => (v === '' ? undefined : v), schema.optional());

export const ClientEnvSchema = z.object({
  VITE_API_URL: z.url(),
  VITE_WORKOS_CLIENT_ID: z.string().min(1),
  // Optional on Vercel preview deploys: when unset, providers.tsx falls back
  // to `${window.location.origin}/auth/callback`, so every per-PR preview
  // alias works without per-deploy env wiring. Production + staging keep an
  // explicit value (predictable, no DOM lookup at boot).
  VITE_WORKOS_REDIRECT_URI: optionalNonEmpty(z.url()),
  // Backend URL for the eco-tools API consumed by the embedded
  // `@noma-eco/*` packages (project-area, N2000, provincial). Distinct from
  // VITE_API_URL (eco-platform's own backend) — the SPA calls the eco-tools
  // backend directly for protected-area checks.
  VITE_ECO_TOOLS_API_URL: z.url(),

  // Sentry / Spotlight — see docs/features/observability/design.md. The DSN,
  // release, and environment are optional in dev (events go to the local
  // Spotlight sidecar); in ci/preview/staging/production the deploy target
  // injects VITE_SENTRY_DSN and a VITE_SENTRY_RELEASE (git SHA).
  // VITE_SENTRY_ENVIRONMENT tags events — `preview` separates per-PR Vercel
  // events from staging in the Sentry dashboard. VITE_SPOTLIGHT_PORT is the
  // host port the local Spotlight sidecar listens on (default 8208 —
  // eco-platform's 8200-block +8 slot; a `-dev` worktree uses 8308); ignored
  // when VITE_SENTRY_DSN is set.
  VITE_SENTRY_DSN: optionalNonEmpty(z.url()),
  VITE_SENTRY_ENVIRONMENT: z
    .enum(['development', 'ci', 'preview', 'staging', 'production'])
    .default('development'),
  VITE_SENTRY_RELEASE: optionalNonEmpty(z.string()),
  VITE_SPOTLIGHT_PORT: z.coerce.number().int().positive().default(8208),

  // PostHog project key. Absent → PostHog stays off entirely (no SDK init,
  // no consent-sync calls, no captures). See posthog-integration/design.md.
  // The EU ingestion host is hardcoded in `initPostHog` — no host env var.
  VITE_POSTHOG_KEY: optionalNonEmpty(z.string()),
});

export type ClientEnv = z.infer<typeof ClientEnvSchema>;

export const clientEnv: ClientEnv = ClientEnvSchema.parse({
  VITE_API_URL: import.meta.env.VITE_API_URL,
  VITE_WORKOS_CLIENT_ID: import.meta.env.VITE_WORKOS_CLIENT_ID,
  VITE_WORKOS_REDIRECT_URI: import.meta.env.VITE_WORKOS_REDIRECT_URI,
  VITE_ECO_TOOLS_API_URL: import.meta.env.VITE_ECO_TOOLS_API_URL,
  VITE_SENTRY_DSN: import.meta.env.VITE_SENTRY_DSN,
  VITE_SENTRY_ENVIRONMENT: import.meta.env.VITE_SENTRY_ENVIRONMENT,
  VITE_SENTRY_RELEASE: import.meta.env.VITE_SENTRY_RELEASE,
  VITE_SPOTLIGHT_PORT: import.meta.env.VITE_SPOTLIGHT_PORT,
  VITE_POSTHOG_KEY: import.meta.env.VITE_POSTHOG_KEY,
});
